import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDataSource } from '../../../lib/dataSource'
import { useIntroNote } from '../../../lib/intro'
import type { Task, Section } from '../../../lib/db/models'
import type { TaskCreationData } from '../components/TaskEntry'
import { toCreateTask } from '../utils/toCreateTask'
import { computeAppendOrders, orderAtEndOf } from '../utils/appendTaskOrders'
import { showCreatedElsewhereToast } from '../utils/createdElsewhereToast'
import { useSoftAddSound } from '@/src/renderer/lib/sounds'
import { getScrollContainer } from '@/src/renderer/lib/dom/getScrollContainer'
import { needsScrollIntoView } from '@/src/renderer/lib/dom/needsScrollIntoView'

/**
 * Sicherheitsabstand zur Kante der Liste. Über ihr liegt ein Verlauf; was
 * darunter steckt, gilt nicht als sichtbar und wird ins Bild geholt.
 */
const SCROLL_EDGE_GUARD = 24

/**
 * Unten liegt die **Listen-Werkzeug-Pille** über der Liste (#229) — was hinter
 * ihr steckt, ist nicht zu sehen. Derselbe Wert wie `LIST_TOOLBAR_CLEARANCE`.
 */
const SCROLL_BOTTOM_GUARD = 88

/** Wo eine neue Aufgabe statt in dieser Liste landet (#234) — siehe `destinationOf`. */
type Elsewhere =
  | { kind: 'project'; target: NonNullable<TaskCreationData['target']> }
  | { kind: 'section'; sectionId: string }
  | { kind: 'upcoming' }

/**
 * **Landet die Aufgabe woanders als dort, wo der Ersteller steht?** `#Projekt` im Titel, ein anderer
 * `/Abschnitt` dieses Projekts — oder in Kurz notiert ein Wann: Dann steht sie in Demnächst
 * (`isQuickNote`). `null`: Sie erscheint, wo getippt wurde.
 */
export function destinationOf(data: TaskCreationData, projectId: string | null, sectionHere: string | null): Elsewhere | null {
  const { target } = data
  if (target && target.projectId !== projectId) return { kind: 'project', target }
  // `#Garten` auf der Seite Garten meint kein anderes Ziel — der Abschnitt bleibt der des Erstellers.
  if (target?.sectionId && target.sectionId !== sectionHere) return { kind: 'section', sectionId: target.sectionId }
  if (!projectId && data.startDate) return { kind: 'upcoming' }
  return null
}

/** A render segment: optional section header followed by a group of tasks */
interface RenderSegment {
  section: Section | null
  tasks: Task[]
  taskCount: number
}

export interface StartCreatingOptions {
  /** Gelandet aus dem Zug von „Neue Aufgabe“ (#301): Der Ersteller startet getönt. */
  landed?: boolean
}

interface UseTaskCreationParams {
  projectId: string | undefined
  /**
   * Lifespace the created task belongs to. Written into `Task.lifeSpace` on
   * every new task so downstream views can filter by lifespace without
   * joining through the project.
   */
  lifeSpace: 'personal' | 'work' | 'home'
  segments: RenderSegment[]
  /**
   * Die Segmente, die die Liste zeigt — in deren Indizes steht der Ersteller (`TaskBoard`). Fehlt es, alle.
   * Zeigt die Liste nur einen Abschnitt (Listen-Modus), ist Index 0 dieser Abschnitt und nicht der erste des
   * Projekts; gespeichert werden muss dort, wo der Ersteller steht (Code-Review #272).
   */
  drawnSegments?: RenderSegment[]
  tasks: Task[] | undefined
  sections: Section[] | undefined
  /**
   * Die Abschnitte der Segmente sind gedacht, nicht gespeichert (Kurz notiert: „Letzte 7 Tage“ / „Älter“, #304).
   * Sie bestimmen weiter, wo der Ersteller steht und welche Reihenfolge die Aufgabe bekommt — gespeichert wird
   * aber `sectionId: null`, auch hinter einer Aufgabe, die die gedachte Kennung noch aus alter Zeit trägt.
   */
  virtualSections?: boolean
  activeSegmentRef: React.RefObject<number>
  navClearSelectionRef: React.RefObject<(() => void) | null>
  navSelectTaskRef: React.RefObject<((taskId: string) => void) | null>
  navSelectSectionRef: React.RefObject<((sectionId: string) => void) | null>
  navFocusedItemRef: React.RefObject<{ type: 'task' | 'section'; id: string } | null>
  setLastInteractedSegmentIndex: (idx: number) => void
}

export function useTaskCreation({
  projectId,
  lifeSpace,
  segments,
  drawnSegments,
  tasks,
  sections,
  virtualSections = false,
  activeSegmentRef,
  navClearSelectionRef,
  navSelectTaskRef,
  navSelectSectionRef,
  navFocusedItemRef,
  setLastInteractedSegmentIndex,
}: UseTaskCreationParams) {
  const drawn = drawnSegments ?? segments
  const playSoftAdd = useSoftAddSound()
  const source = useDataSource()
  const noteIntro = useIntroNote()
  // Jede neue Aufgabe läuft hierüber: Landet sie in Kurz notiert, ist es eine eigene Notiz (#323, Lernaufgabe).
  const createTask = useCallback(async (input: Parameters<typeof source.createTask>[0]) => {
    const created = await source.createTask(input)
    if (input.projectId == null && input.startDate == null && input.pausedAt == null) noteIntro('quicknotes')
    return created
  }, [source, noteIntro])
  const { t } = useTranslation()

  // Active creation: form is visible, user is typing
  const [creatingPosition, setCreatingPosition] = useState<'top' | 'bottom' | 'above' | null>(null)

  // Morph phase: form is collapsing after submit, non-interactive
  const [morphData, setMorphData] = useState<{
    title: string
    position: 'top' | 'bottom' | 'above'
    segmentIdx: number
    selected: boolean
  } | null>(null)

  // Task ID for selection in TaskList + excludeTaskId during morph
  const [autoSelectId, setAutoSelectId] = useState<string | null>(null)

  // Inline creation: creator appears right after a specific task (Cmd+Enter from expanded)
  const [creatingAfterTaskId, setCreatingAfterTaskId] = useState<string | null>(null)
  const creatingAfterTaskIdRef = useRef(creatingAfterTaskId)
  creatingAfterTaskIdRef.current = creatingAfterTaskId

  // Inline morph: title of the just-submitted inline task (creator morphs to collapsed row)
  const [inlineMorphTitle, setInlineMorphTitle] = useState<string | null>(null)
  // Whether the inline morph should show as selected (Enter/stop) or unselected (CMD+Enter/continue)
  const [inlineMorphSelected, setInlineMorphSelected] = useState(false)

  // Incrementing key forces fresh TaskEntry mount (prevents stale form state)
  const [creationKey, setCreationKey] = useState(0)
  const creationKeyRef = useRef(creationKey)
  creationKeyRef.current = creationKey

  /*
    **Gelandet aus dem Zug** (#301): „Neue Aufgabe“ wurde aus der Pille in die Liste gezogen. Die
    Eingabe startet dann getönt und gleitet von der Fläche her. Gilt nur für den Ersteller, der mit
    diesem Start entsteht (sein `creationKey`) — ein weiterer nach ⌘Enter kommt wie gewohnt.
  */
  const [landedKey, setLandedKey] = useState<number | null>(null)

  // Inline reset key: clears form fields without unmounting (Cmd+Enter continue)
  const [inlineResetKey, setInlineResetKey] = useState(0)

  // Flag: after inline morph completes, keep creator in creating mode (don't clear creatingAfterTaskId)
  const continueAfterInlineMorphRef = useRef(false)

  // Key for the morph element during inline continue-morph (two-element mode).
  // Captures the old creationKey so the morph element reuses the same React key
  // for a smooth creating→collapsed transition.
  const [inlineMorphKey, setInlineMorphKey] = useState<number | null>(null)

  // Key for the morph element during positional continue-morph (bottom two-element mode).
  // Same mechanism as inlineMorphKey: captures old creationKey so the morph element
  // reuses the same React key for a smooth creating→collapsed transition.
  const [positionMorphKey, setPositionMorphKey] = useState<number | null>(null)
  const positionMorphKeyRef = useRef<number | null>(null)

  // Stores the new task ID during inline continue-morph so handleInlineMorphComplete
  // can move the creator to after the new task when the morph finishes.
  const pendingContinueTaskRef = useRef<string | null>(null)

  // Pending inline target: after Cmd+Enter, the creator stays at its old position.
  // Once the new task appears via useLiveQuery, we move the creator after it.
  const [pendingInlineTarget, setPendingInlineTarget] = useState<string | null>(null)

  // Deferred inline target: for 'top' position CMD+Enter, we delay setting
  // pendingInlineTarget until after the two-element morph completes, so the
  // morph animation plays fully before the creator repositions to inline.
  const deferredInlineTargetRef = useRef<string | null>(null)

  // When transitioning from positional (top) to inline, skip the inline creator's
  // entrance animation so it appears instantly instead of morphing from small to large.
  // Uses a ref (not state) to avoid race conditions — the ref is read synchronously
  // during render and cleared after the inline creator has mounted.
  const skipInlineEntranceRef = useRef(false)

  // Nav item that was focused before opening the creator — restored on cancel.
  const previousFocusedItemRef = useRef<{ type: 'task' | 'section'; id: string } | null>(null)
  // Last task ID created during this creator session (survives CMD+Enter cycles).
  const lastCreatedTaskIdRef = useRef<string | null>(null)

  // Guard: prevents opening a second creator while one is already active.
  // Manually managed (not render-synced) so it's immune to stale closures.
  const creatorOpenRef = useRef(false)
  // Timestamp of last cancel — blocks re-opening within 300ms to handle the
  // pointerdown→pointerup race (click-outside cancels on pointerdown, then a
  // create button's click fires and would immediately reopen).
  const cancelledAtRef = useRef(0)

  const creatingSegmentIdxRef = useRef<number>(0)
  const topCreateRef = useRef<HTMLDivElement>(null)
  const bottomCreateRef = useRef<HTMLDivElement>(null)

  // Beim Projektwechsel alles aus der Erstellung wegräumen.
  //
  // `TabStack` schlüsselt die Seite nach Tab, nicht nach Projekt
  // (`key={tab.id}`): Wechselt man im selben Tab das Projekt, bleibt dieselbe
  // `ProjectPage` montiert und bekommt nur ein neues `projectId` — der Zustand
  // hier reist mit. Ein halb fertiger Ersteller oder eine hängengebliebene
  // Morph-Attrappe hat im nächsten Projekt nichts verloren; am 21.09.2026 stand
  // so die Attrappe aus einem leeren Projekt neben den echten Aufgaben eines
  // anderen (`.claude/tasks/empty-state-geisterzeile.md`).
  //
  // Ein Riegel, keine Ursachenbehebung: Die liegt in
  // `shouldShowCanvasEmptyState`. Er begrenzt jede künftige Lücke derselben
  // Art auf EINE Seite.
  const previousProjectIdRef = useRef(projectId)
  useEffect(() => {
    if (previousProjectIdRef.current === projectId) return
    previousProjectIdRef.current = projectId

    setCreatingPosition(null)
    setCreatingAfterTaskId(null)
    setMorphData(null)
    setPositionMorphKey(null)
    positionMorphKeyRef.current = null
    setInlineMorphTitle(null)
    setPendingInlineTarget(null)
    setAutoSelectId(null)
    deferredInlineTargetRef.current = null
    creatorOpenRef.current = false
  }, [projectId])

  // Clean up autoSelectId once the task appears in the list and all morphs are done.
  // Gates on both morphData (top/bottom morph) and inlineMorphTitle (inline morph)
  // so autoSelectId isn't cleared while the task is still excluded from the list.
  useEffect(() => {
    if (autoSelectId && !morphData && !inlineMorphTitle && tasks?.some(t => t.id === autoSelectId)) {
      setAutoSelectId(null)
    }
  }, [autoSelectId, morphData, inlineMorphTitle, tasks])

  // Move inline creator to after the newly created task once it appears via useLiveQuery.
  // Until then, the creator stays at its old position (no unmount/remount).
  // Also clears creatingPosition when transitioning from positional (top/above) to inline.
  useEffect(() => {
    if (pendingInlineTarget && tasks?.some(t => t.id === pendingInlineTarget)) {
      if (creatingPosition) setCreatingPosition(null)
      setCreatingAfterTaskId(pendingInlineTarget)
      setPendingInlineTarget(null)
      setInlineResetKey(k => k + 1)
    }
  }, [pendingInlineTarget, tasks, creatingPosition])

  // skipInlineEntranceRef is reset in startCreating / startCreatingAfter / handleCancelCreate
  // so it stays true across renders until the user opens a new creator or cancels.

  /*
    **Den Ersteller ins Bild holen — aber nur, wenn er nicht schon dasteht.**

    Ohne die Frage davor lief das hier IMMER, und `behavior: 'smooth'` zieht
    danach eine Viertelsekunde nach, bis anderthalb Pixel übrig sind — eine
    Bewegung ohne Anlass, wenn der Ersteller ohnehin im Bild steht.

    Gefunden beim Suchen nach dem „Hüpfen" (21.09.2026). Dessen Ursache war es
    NICHT: Das Protokoll zeigte nach dem Enter gar keinen Scroll-Befehl, und der
    Sprung kam von einer toten Abstandsregel
    (`.claude/tasks/creation-entry-gap-jump.md`).
  */
  // Scroll bottom creation entry into view after expand animation finishes.
  useEffect(() => {
    if (creatingPosition !== 'bottom') return
    const el = bottomCreateRef.current
    if (!el) return
    // Nach dem Segment, in dem der Ersteller steht — nicht nach dem zuletzt
    // benutzten. Die Listen-Werkzeug-Pille legt im Abschnitt an, der im Bild
    // steht; das „Ende des Projekts" läge sonst weit unter dem Ersteller.
    const isLastSegment = creatingSegmentIdxRef.current >= drawn.length - 1

    const timer = setTimeout(() => {
      if (!needsScrollIntoView(el, SCROLL_BOTTOM_GUARD)) return
      if (isLastSegment) {
        const scrollContainer = getScrollContainer(el)
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' })
        }
      } else {
        el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [creatingPosition, drawn.length])

  // Scroll top creation entry into view after expand animation finishes.
  useEffect(() => {
    if (creatingPosition !== 'top' && creatingPosition !== 'above') return
    const el = topCreateRef.current
    if (!el) return
    const timer = setTimeout(() => {
      if (!needsScrollIntoView(el, SCROLL_EDGE_GUARD)) return
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }, 300)
    return () => clearTimeout(timer)
  }, [creatingPosition])

  /** Der Ersteller geht zu, ohne Morph und ohne eine Zeile zu wählen. */
  const closeCreator = useCallback(() => {
    creatorOpenRef.current = false
    cancelledAtRef.current = Date.now()
    setAutoSelectId(null)
    setMorphData(null)
    setPositionMorphKey(null)
    positionMorphKeyRef.current = null
    deferredInlineTargetRef.current = null
    skipInlineEntranceRef.current = false
    setCreatingPosition(null)
    setCreatingAfterTaskId(null)
    setInlineMorphTitle(null)
    setPendingInlineTarget(null)
  }, [])

  const restoreSelection = useCallback(() => {
    const lastCreated = lastCreatedTaskIdRef.current
    const prevItem = previousFocusedItemRef.current
    previousFocusedItemRef.current = null
    if (lastCreated) {
      navSelectTaskRef.current?.(lastCreated)
    } else if (prevItem) {
      if (prevItem.type === 'task') navSelectTaskRef.current?.(prevItem.id)
      else navSelectSectionRef.current?.(prevItem.id)
    }
  }, [navSelectSectionRef, navSelectTaskRef])

  /*
    **Woanders anlegen** (#234): Hier gibt es keine Ersatzzeile und keinen Morph — die Aufgabe käme in
    dieser Liste nie an, der Ersteller bliebe hängen. Enter schließt ihn, ⌘↵ leert ihn für die nächste.
    Ein anderer Abschnitt desselben Projekts ist zu sehen: kein Toast, die Aufgabe wird ausgewählt.
    Sonst sagt ein Toast, wo sie steht.
  */
  const createElsewhere = useCallback(async (data: TaskCreationData, where: Elsewhere, keepOpen: boolean) => {
    const newTaskId = crypto.randomUUID()
    if (keepOpen) {
      if (creatingAfterTaskIdRef.current) setInlineResetKey(k => k + 1)
      else setCreationKey(k => k + 1)
    } else {
      closeCreator()
      if (where.kind === 'section') {
        previousFocusedItemRef.current = null
        setAutoSelectId(newTaskId)
      } else {
        restoreSelection()
      }
    }

    if (where.kind === 'section') {
      const [order] = computeAppendOrders({ tasks: tasks ?? [], sections: sections ?? [], sectionId: where.sectionId, count: 1 })
      await createTask(toCreateTask(data, { id: newTaskId, lifeSpace, projectId: projectId ?? null, sectionId: where.sectionId, order }))
      return
    }

    let created: Task
    let place: string
    if (where.kind === 'project') {
      const { target } = where
      const order = await orderAtEndOf(source, target.projectId, target.sectionId)
      created = await createTask(toCreateTask(data, {
        id: newTaskId, lifeSpace: target.lifeSpace, projectId: target.projectId, sectionId: target.sectionId, order,
      }))
      place = target.projectName || t('project.defaultName')
    } else {
      created = await createTask(toCreateTask(data, { id: newTaskId, lifeSpace, projectId: null, sectionId: null }))
      place = t('upcoming.title')
    }
    showCreatedElsewhereToast(t, place, created)
  }, [closeCreator, restoreSelection, tasks, sections, source, createTask, lifeSpace, projectId, t])

  const handleCreateTask = useCallback(async (data: TaskCreationData) => {
    if (!creatingPosition) return

    const segIdx = creatingSegmentIdxRef.current
    const pos = creatingPosition
    const elsewhere = destinationOf(
      data, projectId ?? null, pos === 'above' || virtualSections ? null : ((drawn[segIdx] ?? drawn[0])?.section?.id ?? null),
    )
    if (elsewhere) { await createElsewhere(data, elsewhere, false); return }

    // ── Immediately: start morph, free keyboard shortcuts ──
    // 1. Blur input so focus goes to body and shortcuts work
    const ref = (pos === 'top' || pos === 'above') ? topCreateRef : bottomCreateRef
    if (ref.current?.contains(document.activeElement)) {
      ;(document.activeElement as HTMLElement)?.blur()
    }

    // 2. Set morphData → TaskEntry switches to mode="collapsed"
    setMorphData({ title: data.title, position: pos, segmentIdx: segIdx, selected: true })
    // 3. Clear creatingPosition → keyboard shortcut guard passes
    setCreatingPosition(null)
    // 4. Keep segment active during morph so AddTask stays in the right place
    setLastInteractedSegmentIndex(segIdx)

    // ── Async: compute order and create task in DB ──
    // 'above' creates an unsectioned task before all sections
    const isAbove = pos === 'above'
    const seg = isAbove ? null : (drawn[segIdx] ?? drawn[0])
    const sectionId = isAbove || virtualSections ? null : (seg?.section?.id ?? null)

    let order: number | undefined

    if (isAbove) {
      // Place before the first section — always in the unsectioned area at the top.
      const allItems = [
        ...(tasks ?? []).map(t => ({ id: t.id, order: t.order, isSection: false })),
        ...(sections ?? []).map(s => ({ id: s.id, order: s.order, isSection: true })),
      ].sort((a, b) => a.order - b.order)

      // Place before ALL items (unsectioned tasks + sections).
      // Using firstSection.order - 1 would collide with unsectioned tasks
      // that already occupy orders just below the section.
      order = allItems.length > 0 ? allItems[0].order - 1 : 0
    } else if (seg) {
      const segTasks = seg.tasks
      const sectionOrder = seg.section?.order ?? 0

      const allItems = [
        ...(tasks ?? []).map(t => ({ id: t.id, order: t.order })),
        ...(sections ?? []).map(s => ({ id: s.id, order: s.order })),
      ].sort((a, b) => a.order - b.order)

      const renormalizeSegment = async (baseOrder: number, reserveFirst: boolean) => {
        const offset = reserveFirst ? 2 : 1
        await source.reorderTasks(segTasks.map((t, i) => ({
          id: t.id, order: baseOrder + i + offset,
        })))
        const maxTaskOrder = baseOrder + segTasks.length + (reserveFirst ? 1 : 0)
        // Der nächste Abschnitt im Projekt, nicht in der Ansicht — zeigt sie nur einen, gibt es dort keinen.
        const nextSegSection = segments[segments.indexOf(seg) + 1]?.section
        if (nextSegSection && nextSegSection.order <= maxTaskOrder + 1) {
          await source.updateSection(nextSegSection.id, { order: maxTaskOrder + 2 })
        }
      }

      if (pos === 'top') {
        if (segTasks.length > 0) {
          const firstIdx = allItems.findIndex(item => item.id === segTasks[0].id)
          const prevOrder = firstIdx > 0 ? allItems[firstIdx - 1].order : segTasks[0].order - 1

          if (prevOrder < segTasks[0].order) {
            order = (prevOrder + segTasks[0].order) / 2
          } else {
            await renormalizeSegment(sectionOrder, true)
            order = sectionOrder + 1
          }
        } else {
          // Empty section — place between section header and whatever follows
          const secIdx = allItems.findIndex(item => item.id === seg.section?.id)
          const nextItem = secIdx >= 0 && secIdx < allItems.length - 1
            ? allItems[secIdx + 1]
            : null
          if (nextItem && nextItem.order > sectionOrder) {
            order = (sectionOrder + nextItem.order) / 2
          } else if (nextItem) {
            order = sectionOrder + 0.5
            const nextSection = sections?.find(s => s.id === nextItem.id)
            if (nextSection && nextSection.order <= order) {
              await source.updateSection(nextSection.id, { order: order + 0.5 })
            }
          } else {
            order = sectionOrder + 0.5
          }
        }
      } else {
        if (segTasks.length > 0) {
          const lastTask = segTasks[segTasks.length - 1]
          const lastIdx = allItems.findIndex(item => item.id === lastTask.id)
          const nextItem = lastIdx >= 0 && lastIdx < allItems.length - 1
            ? allItems[lastIdx + 1]
            : null

          if (!nextItem || nextItem.order > lastTask.order) {
            order = nextItem
              ? (lastTask.order + nextItem.order) / 2
              : lastTask.order + 1
          } else {
            await renormalizeSegment(sectionOrder, false)
            order = sectionOrder + segTasks.length + 1
          }
        } else {
          const secIdx = allItems.findIndex(item => item.id === seg.section?.id)
          const nextItem = secIdx >= 0 && secIdx < allItems.length - 1
            ? allItems[secIdx + 1]
            : null

          if (!nextItem || nextItem.order > sectionOrder) {
            order = nextItem
              ? (sectionOrder + nextItem.order) / 2
              : sectionOrder + 0.5
          } else {
            order = sectionOrder + 1
            const nextSection = sections?.find(s => s.id === nextItem.id)
            if (nextSection && nextSection.order <= order) {
              await source.updateSection(nextSection.id, { order: order + 1 })
            }
          }
        }
      }
    }

    // Pre-generate ID and set state BEFORE createTask so excludeTaskId is
    // active when useLiveQuery delivers the new task. Prevents height animation.
    const newTaskId = crypto.randomUUID()
    setAutoSelectId(newTaskId)
    lastCreatedTaskIdRef.current = newTaskId

    await createTask(toCreateTask(data, { id: newTaskId, lifeSpace, projectId: projectId ?? null, sectionId, order }))
  }, [projectId, lifeSpace, creatingPosition, segments, drawn, virtualSections, source, createTask, tasks, sections, setLastInteractedSegmentIndex, createElsewhere])

  /**
   * Cmd/Ctrl+Enter in creating mode: save task and keep creator open.
   * For above: morph to collapsed, then transition to inline creation.
   * For top/bottom: two-element morph (old collapses, new creator appears).
   */
  const handleCreateTaskAndContinue = useCallback(async (data: TaskCreationData) => {
    if (!creatingPosition) return

    const segIdx = creatingSegmentIdxRef.current
    const pos = creatingPosition
    const isAboveContinue = pos === 'above'
    const seg = isAboveContinue ? null : (drawn[segIdx] ?? drawn[0])
    const sectionId = isAboveContinue || virtualSections ? null : (seg?.section?.id ?? null)
    const elsewhere = destinationOf(data, projectId ?? null, sectionId)
    if (elsewhere) { await createElsewhere(data, elsewhere, true); return }

    // For 'above': single-element morph, then transition to inline.
    // Creator is outside the TaskList, so two-element morph would cause a positional jump.
    if (isAboveContinue) {
      const ref = topCreateRef
      if (ref.current?.contains(document.activeElement)) {
        ;(document.activeElement as HTMLElement)?.blur()
      }
      setMorphData({ title: data.title, position: pos, segmentIdx: segIdx, selected: false })
      setCreatingPosition(null)
      setLastInteractedSegmentIndex(segIdx)
    }

    let order: number | undefined

    if (isAboveContinue) {
      // Place before ALL items — same logic as handleCreateTask for 'above'.
      // The creator then transitions to inline (below the new task), so subsequent
      // tasks chain correctly via handleCreateTaskAfterAndContinue.
      const allItems = [
        ...(tasks ?? []).map(t => ({ id: t.id, order: t.order })),
        ...(sections ?? []).map(s => ({ id: s.id, order: s.order })),
      ].sort((a, b) => a.order - b.order)

      order = allItems.length > 0 ? allItems[0].order - 1 : 0
    } else if (seg) {
      const segTasks = seg.tasks
      const sectionOrder = seg.section?.order ?? 0

      const allItems = [
        ...(tasks ?? []).map(t => ({ id: t.id, order: t.order })),
        ...(sections ?? []).map(s => ({ id: s.id, order: s.order })),
      ].sort((a, b) => a.order - b.order)

      const renormalizeSegment = async (baseOrder: number, reserveFirst: boolean) => {
        const offset = reserveFirst ? 2 : 1
        await source.reorderTasks(segTasks.map((t, i) => ({
          id: t.id, order: baseOrder + i + offset,
        })))
        const maxTaskOrder = baseOrder + segTasks.length + (reserveFirst ? 1 : 0)
        // Der nächste Abschnitt im Projekt, nicht in der Ansicht — zeigt sie nur einen, gibt es dort keinen.
        const nextSegSection = segments[segments.indexOf(seg) + 1]?.section
        if (nextSegSection && nextSegSection.order <= maxTaskOrder + 1) {
          await source.updateSection(nextSegSection.id, { order: maxTaskOrder + 2 })
        }
      }

      if (pos === 'top') {
        if (segTasks.length > 0) {
          const firstIdx = allItems.findIndex(item => item.id === segTasks[0].id)
          const prevOrder = firstIdx > 0 ? allItems[firstIdx - 1].order : segTasks[0].order - 1
          if (prevOrder < segTasks[0].order) {
            order = (prevOrder + segTasks[0].order) / 2
          } else {
            await renormalizeSegment(sectionOrder, true)
            order = sectionOrder + 1
          }
        } else {
          // Empty section — place between section header and whatever follows
          const secIdx = allItems.findIndex(item => item.id === seg.section?.id)
          const nextItem = secIdx >= 0 && secIdx < allItems.length - 1
            ? allItems[secIdx + 1]
            : null
          if (nextItem && nextItem.order > sectionOrder) {
            order = (sectionOrder + nextItem.order) / 2
          } else if (nextItem) {
            order = sectionOrder + 0.5
            const nextSection = sections?.find(s => s.id === nextItem.id)
            if (nextSection && nextSection.order <= order) {
              await source.updateSection(nextSection.id, { order: order + 0.5 })
            }
          } else {
            order = sectionOrder + 0.5
          }
        }
      } else {
        if (segTasks.length > 0) {
          const lastTask = segTasks[segTasks.length - 1]
          const lastIdx = allItems.findIndex(item => item.id === lastTask.id)
          const nextItem = lastIdx >= 0 && lastIdx < allItems.length - 1
            ? allItems[lastIdx + 1]
            : null
          if (!nextItem || nextItem.order > lastTask.order) {
            order = nextItem
              ? (lastTask.order + nextItem.order) / 2
              : lastTask.order + 1
          } else {
            await renormalizeSegment(sectionOrder, false)
            order = sectionOrder + segTasks.length + 1
          }
        } else {
          const secIdx = allItems.findIndex(item => item.id === seg.section?.id)
          const nextItem = secIdx >= 0 && secIdx < allItems.length - 1
            ? allItems[secIdx + 1]
            : null
          if (!nextItem || nextItem.order > sectionOrder) {
            order = nextItem
              ? (sectionOrder + nextItem.order) / 2
              : sectionOrder + 0.5
          } else {
            order = sectionOrder + 1
            const nextSection = sections?.find(s => s.id === nextItem.id)
            if (nextSection && nextSection.order <= order) {
              await source.updateSection(nextSection.id, { order: order + 1 })
            }
          }
        }
      }
    }

    // Pre-generate ID and set state BEFORE createTask so excludeTaskId is
    // active when useLiveQuery delivers the new task. Prevents height animation.
    const newTaskId = crypto.randomUUID()
    lastCreatedTaskIdRef.current = newTaskId

    if (isAboveContinue) {
      // Above: single-element morph, then transition to inline after task appears.
      setPendingInlineTarget(newTaskId)
      setAutoSelectId(newTaskId)
    } else {
      // Top/Bottom: two-element morph (old creator collapses, new creator appears)
      const ref = pos === 'top' ? topCreateRef : bottomCreateRef
      if (ref.current?.contains(document.activeElement)) {
        ;(document.activeElement as HTMLElement)?.blur()
      }
      positionMorphKeyRef.current = creationKey
      setPositionMorphKey(creationKey)
      setMorphData({ title: data.title, position: pos, segmentIdx: segIdx, selected: false })
      setCreationKey(k => k + 1)
      setLastInteractedSegmentIndex(segIdx)
      setAutoSelectId(newTaskId)
      // Top: after morph completes, transition creator to inline (below the created task).
      if (pos === 'top') {
        deferredInlineTargetRef.current = newTaskId
      }
    }

    await createTask(toCreateTask(data, { id: newTaskId, lifeSpace, projectId: projectId ?? null, sectionId, order }))
  }, [projectId, lifeSpace, creatingPosition, segments, drawn, virtualSections, creationKey, source, createTask, tasks, sections, setLastInteractedSegmentIndex, createElsewhere])

  // Called by TaskEntry.onMorphComplete (300ms after creating → collapsed transition)
  const handleMorphComplete = useCallback(() => {
    const wasPositionContinue = positionMorphKeyRef.current != null
    setMorphData(null)
    setPositionMorphKey(null)
    positionMorphKeyRef.current = null

    // Top CMD+Enter: morph done → transition creator directly to inline (below the created task).
    // All state changes batch into one render so green unmounts + inline mounts simultaneously.
    const deferredTarget = deferredInlineTargetRef.current
    if (deferredTarget) {
      deferredInlineTargetRef.current = null
      skipInlineEntranceRef.current = true
      setCreatingPosition(null)
      setCreatingAfterTaskId(deferredTarget)
      setInlineResetKey(k => k + 1)
      setAutoSelectId(null)
      return
    }

    if (creatingAfterTaskIdRef.current || wasPositionContinue) {
      // Inline creation follows (above) or position creator still active (bottom continue)
      setAutoSelectId(null)
    } else {
      // No inline creation follows → creation cycle complete
      creatorOpenRef.current = false
    }
  }, [])

  // Called by inline creator's onMorphComplete (300ms after creating → collapsed morph)
  const handleInlineMorphComplete = useCallback(() => {
    setInlineMorphTitle(null)
    setInlineMorphKey(null)
    if (continueAfterInlineMorphRef.current) {
      // Cmd+Enter continue: morph element disappears, real task takes its place,
      // creator moves to after the new task.
      continueAfterInlineMorphRef.current = false
      setAutoSelectId(null)
      if (pendingContinueTaskRef.current) {
        setCreatingAfterTaskId(pendingContinueTaskRef.current)
        pendingContinueTaskRef.current = null
      }
    } else {
      // Enter stop: remove creator entirely, task becomes visible via autoSelectId.
      creatorOpenRef.current = false
      setCreatingAfterTaskId(null)
    }
  }, [])

  const handleCancelCreate = useCallback(() => {
    closeCreator()
    // Select the last created task (CMD+Enter) or restore the previously focused item.
    restoreSelection()
  }, [closeCreator, restoreSelection])

  // Freeze segment index before opening the creation entry
  // `true`, wenn der Ersteller aufgeht — gesperrt ist er, solange einer offen ist oder eben abgebrochen wurde.
  const startCreating = useCallback((position: 'top' | 'bottom' | 'above', segmentIndex?: number, options?: StartCreatingOptions): boolean => {
    if (creatorOpenRef.current) return false
    if (Date.now() - cancelledAtRef.current < 300) return false
    creatorOpenRef.current = true
    setLandedKey(options?.landed ? creationKeyRef.current + 1 : null)
    playSoftAdd()
    // 'above' always renders at segment 0 (above the first section header),
    // regardless of which segment is currently active/hovered.
    // When segmentIndex is provided (click on AddTask), it overrides the
    // selection-based activeSegmentRef (keyboard shortcuts omit it).
    creatingSegmentIdxRef.current = position === 'above' ? 0 : (segmentIndex ?? activeSegmentRef.current)
    setAutoSelectId(null)
    setMorphData(null)
    setPositionMorphKey(null)
    positionMorphKeyRef.current = null
    deferredInlineTargetRef.current = null
    skipInlineEntranceRef.current = false
    setCreatingAfterTaskId(null)
    setInlineMorphTitle(null)
    setPendingInlineTarget(null)
    setCreationKey(k => k + 1)
    setCreatingPosition(position)
    previousFocusedItemRef.current = navFocusedItemRef.current
    lastCreatedTaskIdRef.current = null
    navClearSelectionRef.current?.()
    return true
  }, [activeSegmentRef, navClearSelectionRef, navFocusedItemRef, playSoftAdd])

  // ── Inline creation: after a specific task (Cmd+Enter from expanded) ──

  const startCreatingAfter = useCallback((taskId: string, options?: StartCreatingOptions): boolean => {
    if (creatorOpenRef.current) return false
    if (Date.now() - cancelledAtRef.current < 300) return false
    creatorOpenRef.current = true
    setLandedKey(options?.landed ? creationKeyRef.current + 1 : null)
    playSoftAdd()
    setCreatingAfterTaskId(taskId)
    setCreatingPosition(null)
    setAutoSelectId(null)
    setMorphData(null)
    setPositionMorphKey(null)
    positionMorphKeyRef.current = null
    deferredInlineTargetRef.current = null
    skipInlineEntranceRef.current = false
    setInlineMorphTitle(null)
    setPendingInlineTarget(null)
    setCreationKey(k => k + 1)
    previousFocusedItemRef.current = navFocusedItemRef.current
    lastCreatedTaskIdRef.current = null
    navClearSelectionRef.current?.()
    return true
  }, [navClearSelectionRef, navFocusedItemRef, playSoftAdd])

  /** Compute order for a task inserted right after `afterTaskId`.
   *  When adjacent items have colliding orders (equal or inverted), renormalizes
   *  subsequent items in the DB to create space before computing the midpoint. */
  const computeOrderAfter = useCallback(async (afterTaskId: string): Promise<number | undefined> => {
    const targetTask = tasks?.find(t => t.id === afterTaskId)
    if (!targetTask) return undefined

    const allItems = [
      ...(tasks ?? []).map(t => ({ id: t.id, order: t.order, type: 'task' as const })),
      ...(sections ?? []).map(s => ({ id: s.id, order: s.order, type: 'section' as const })),
    ].sort((a, b) => a.order - b.order)

    const targetIdx = allItems.findIndex(item => item.id === targetTask.id)
    const nextItem = targetIdx >= 0 && targetIdx < allItems.length - 1
      ? allItems[targetIdx + 1]
      : null

    // Normal case: enough room between target and next item
    if (!nextItem || nextItem.order > targetTask.order) {
      const result = nextItem
        ? (targetTask.order + nextItem.order) / 2
        : targetTask.order + 1

      return result
    }

    // Collision: next item has order <= target order.
    // Renormalize all items after target with integer spacing to create room.

    const baseOrder = targetTask.order
    const itemsAfterTarget = allItems.slice(targetIdx + 1)
    const taskUpdates: { id: string; order: number }[] = []

    for (let i = 0; i < itemsAfterTarget.length; i++) {
      const item = itemsAfterTarget[i]
      const newOrder = baseOrder + i + 1
      if (item.order !== newOrder) {
        if (item.type === 'task') {
          taskUpdates.push({ id: item.id, order: newOrder })
        } else {
          await source.updateSection(item.id, { order: newOrder })
        }
      }
    }

    if (taskUpdates.length > 0) {
      await source.reorderTasks(taskUpdates)
    }

    // Now target is at baseOrder, next item is at baseOrder+1 → place new task at midpoint
    return baseOrder + 0.5
  }, [tasks, sections, source])

  const handleCreateTaskAfter = useCallback(async (data: TaskCreationData) => {
    if (!creatingAfterTaskId) return

    // During two-element morph (CMD+Enter continue), creatingAfterTaskId is stale
    // (still points to old target). Use pendingContinueTaskRef to compute order
    // after the LATEST created task so new tasks chain correctly.
    const effectiveTargetId = pendingContinueTaskRef.current ?? creatingAfterTaskId
    const targetTask = tasks?.find(t => t.id === effectiveTargetId)
      ?? tasks?.find(t => t.id === creatingAfterTaskId)
    if (!targetTask) return
    const sectionId = virtualSections ? null : targetTask.sectionId
    const elsewhere = destinationOf(data, projectId ?? null, sectionId)
    if (elsewhere) { await createElsewhere(data, elsewhere, false); return }

    const order = await computeOrderAfter(targetTask.id)

    // Pre-generate ID and set state BEFORE createTask so excludeTaskId is
    // active when useLiveQuery delivers the new task. Prevents height animation.
    const newTaskId = crypto.randomUUID()

    setAutoSelectId(newTaskId)
    lastCreatedTaskIdRef.current = newTaskId
    setInlineMorphSelected(true)
    setInlineMorphTitle(data.title)
    // Don't clear creatingAfterTaskId — creator stays mounted in morphing (collapsed) state.
    // handleInlineMorphComplete will clear both after the CSS transition finishes.

    await createTask(toCreateTask(data, { id: newTaskId, lifeSpace, projectId: projectId ?? null, sectionId, order }))
  }, [projectId, lifeSpace, virtualSections, creatingAfterTaskId, tasks, computeOrderAfter, createTask, createElsewhere])

  const handleCreateTaskAfterAndContinue = useCallback(async (data: TaskCreationData) => {
    if (!creatingAfterTaskId) return

    // During two-element morph (rapid CMD+Enter), creatingAfterTaskId is stale
    // (still points to old target). Use pendingContinueTaskRef to compute order
    // after the LATEST created task so new tasks chain correctly.
    const effectiveTargetId = pendingContinueTaskRef.current ?? creatingAfterTaskId
    const targetTask = tasks?.find(t => t.id === effectiveTargetId)
      ?? tasks?.find(t => t.id === creatingAfterTaskId)
    if (!targetTask) return
    const sectionId = virtualSections ? null : targetTask.sectionId
    const elsewhere = destinationOf(data, projectId ?? null, sectionId)
    if (elsewhere) { await createElsewhere(data, elsewhere, true); return }

    const order = await computeOrderAfter(targetTask.id)

    // Pre-generate ID and set state BEFORE createTask so excludeTaskId is
    // active when useLiveQuery delivers the new task. Prevents height animation.
    const newTaskId = crypto.randomUUID()

    // Two-element morph: the old inline creator morphs to collapsed (same React key),
    // while a new empty creator (new key) animates in simultaneously below it.
    setInlineMorphKey(creationKey)         // capture old key for morph element
    setCreationKey(k => k + 1)            // new key for fresh creator
    setInlineMorphSelected(false)
    setInlineMorphTitle(data.title)
    setAutoSelectId(newTaskId)            // hide real task during morph
    lastCreatedTaskIdRef.current = newTaskId
    continueAfterInlineMorphRef.current = true
    pendingContinueTaskRef.current = newTaskId

    await createTask(toCreateTask(data, { id: newTaskId, lifeSpace, projectId: projectId ?? null, sectionId, order }))
  }, [projectId, lifeSpace, virtualSections, creatingAfterTaskId, tasks, computeOrderAfter, creationKey, createTask, createElsewhere])

  return {
    creatingPosition,
    creatingAfterTaskId,
    creatingSegmentIdx: creatingSegmentIdxRef.current,
    pendingInlineTarget,
    morphData,
    positionMorphKey,
    inlineMorphTitle,
    inlineMorphSelected,
    inlineMorphKey,
    creationKey,
    landed: landedKey === creationKey,
    inlineResetKey,
    skipInlineEntrance: skipInlineEntranceRef.current,
    autoSelectId,
    startCreating,
    startCreatingAfter,
    handleCreateTask,
    handleCreateTaskAndContinue,
    handleCreateTaskAfter,
    handleCreateTaskAfterAndContinue,
    handleCancelCreate,
    handleMorphComplete,
    handleInlineMorphComplete,
    topCreateRef,
    bottomCreateRef,
  }
}
